import React, { useRef, useMemo, useState, useLayoutEffect } from 'react'
import useInputDropdownKeyNav from '../../../hooks/useInputDropdownKeyNav'
import styles from './index.module.css'

const MAX_LIST_HEIGHT = 400
const MIN_LIST_HEIGHT = 100
// keeps the list from touching the edge of the window
const WINDOW_MARGIN = 10

/* Decides whether the options list opens below or above the input, and how tall it can be.
   inputTop / inputBottom are window coordinates of the input, listHeight is the full height of the options. */
export const getListPlacement = ({ inputTop, inputBottom, windowHeight, listHeight }) => {
    const spaceBelow = windowHeight - inputBottom - WINDOW_MARGIN
    const spaceAbove = inputTop - WINDOW_MARGIN
    const neededHeight = Math.min(listHeight, MAX_LIST_HEIGHT)

    const openUp = spaceBelow < neededHeight && spaceAbove > spaceBelow
    const availableSpace = openUp ? spaceAbove : spaceBelow

    return {
        openUp,
        maxHeight: Math.max(Math.min(availableSpace, MAX_LIST_HEIGHT), MIN_LIST_HEIGHT),
    }
}

const InputDropdown = (
    {
        value,
        setValue,
        placeholder,
        type,
        setOption,
        returnOption,
        isDisabled,
        setIsOnFocus,
        setIsInputShown,
        inputOptions,
        isInputDropdownBad,
        isOnFocus,
        customContainerStyle = {},
        customStyle = {},
        autoFocus = false,
        inputId,
    }) => {
    const name = type + 'name'
    const id = type + 'id'
    const [oldValue, setOldValue] = useState(null)
    const [currentOptionIndex, setCurrentOptionIndex] = useState(-1)
    const containerRef = useRef(null)
    const inputRef = useRef(null)
    const optionsRef = useRef(null)
    const [listPlacement, setListPlacement] = useState({ openUp: false, maxHeight: MAX_LIST_HEIGHT })

    // When the list opens, it is shown above the input if it does not fit below it (e.g. for the last
    // rows of the time card) and there is more room above. Decided once per opening, so it does not
    // jump while typing. Runs before the browser paints, so the list never flickers in the wrong place.
    useLayoutEffect(() => {
        if (!isOnFocus || !inputRef.current || !optionsRef.current) return

        const inputRect = inputRef.current.getBoundingClientRect()
        setListPlacement(getListPlacement({
            inputTop: inputRect.top,
            inputBottom: inputRect.bottom,
            windowHeight: window.innerHeight,
            listHeight: optionsRef.current.scrollHeight,
        }))
    }, [isOnFocus])

    const sortedSearchFilteredOptions = useMemo(() => {
        return inputOptions.filter(option => option[name].toLowerCase().includes(value.toLowerCase()))
            .sort((a, b) => a[name].localeCompare(b[name]))
    }, [inputOptions, name, value])

    useInputDropdownKeyNav({
        optionNameKey: name,
        isOnFocus,
        currentOptionIndex,
        setCurrentOptionIndex,
        setValue,
        sortedOptions: sortedSearchFilteredOptions,
        inputRef,
        containerRef
    })

    const onFocus = async () => {
        setIsOnFocus(true)
        setOldValue(value)
    }

    const onBlur = () => {
        setIsOnFocus(false)
        setCurrentOptionIndex(-1)

        if (setIsInputShown) {
            setTimeout(() => {
                setIsInputShown(false)
            }, 1)
        }

        if (oldValue === value) {
            return
        }

        const option = inputOptions.find(opt => opt[name] === value)
        if (option) {
            setOption(option, type)
            return
        }

        if (type !== 'client' && type !== 'project') {
            const emptyOption = {
                [name]: value,
                [id]: ''
            }

            setOption(emptyOption, type)
        }
    }

    const selectOption = (opt) => {
        setValue(opt[name])
    }

    const handleChange = (e) => {
        setValue(e.target.value)

        if (returnOption) {
            returnOption(e.target.value)
        }
    }

    return (
        <div className={`${styles.container} input-dropdown`} style={customContainerStyle} ref={containerRef}>
            <input
                autoComplete='off'
                value={value}
                placeholder={placeholder}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={handleChange}
                className={`${styles.input} ${isInputDropdownBad && styles['bad-input']}`}
                style={customStyle}
                disabled={isDisabled}
                autoFocus={autoFocus}
                ref={inputRef}
                id={inputId}
            />
            { isOnFocus && 
                <div
                    ref={optionsRef}
                    className={`${styles['options-container']} ${listPlacement.openUp ? styles['open-up'] : ''}`}
                    style={{ maxHeight: listPlacement.maxHeight }}
                >
                    {sortedSearchFilteredOptions.map((opt, i) => {
                        return (
                            <div
                                key={i}
                                onMouseDown={() => selectOption(opt)}
                                onMouseEnter={() => setCurrentOptionIndex(i)}
                                onMouseLeave={() => setCurrentOptionIndex(-1)}
                                className={`${styles.option} ${i === currentOptionIndex && styles.hover}`}
                            >
                                {opt[name]}
                            </div>)
                    })}
                </div>
            }
        </div>
    )
}

export default InputDropdown
